import { axisBottom } from 'd3-axis';
import { scaleLinear } from 'd3-scale';
import { select } from 'd3-selection';
import 'd3-transition';
import { useEffect, useRef } from 'react';

import {
  historyTicks,
  historyGeometry,
  historyMarkClassName,
  markPosition,
  type HistoryLayout,
} from '../lib/deck-history.ts';

export function DeckHistoryD3({ layout }: { layout: HistoryLayout }) {
  const svgRef = useRef<SVGSVGElement>(null);
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const node = select(svg);
    const x = scaleLinear().domain([0, layout.width]).range([0, layout.width]);
    const ticks = historyTicks(layout);
    const axis = axisBottom(x)
      .tickValues(ticks.map((tick) => tick.value))
      .tickSize(0)
      .tickFormat(
        (value) =>
          ticks.find((tick) => tick.value === Number(value))?.label ?? '',
      );
    const axisGroup = node
      .select<SVGGElement>('g.history-d3-axis')
      .attr(
        'transform',
        `translate(0,${layout.height - historyGeometry.axisHeight})`,
      )
      .call(axis);
    axisGroup.selectAll('path').attr('class', 'hidden');
    axisGroup.selectAll('line').attr('class', 'hidden');
    axisGroup
      .selectAll<SVGTextElement, unknown>('text')
      .attr(
        'class',
        'fill-muted-foreground text-xs [font-family:var(--font-sans)]',
      );

    const marks = node
      .select<SVGGElement>('g.history-d3-marks')
      .selectAll<SVGRectElement, (typeof layout.marks)[number]>('rect')
      .data(layout.marks, (mark) => mark.event.id);
    marks.exit().remove();
    const entered = marks
      .enter()
      .append('rect')
      .attr('height', historyGeometry.markHeight)
      .attr('rx', historyGeometry.markRadius)
      .attr('width', historyGeometry.markWidth);
    const merged = entered.merge(marks);
    merged.attr('stroke-width', historyGeometry.markStrokeWidth);
    const reduceMotion = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    const applyMarks = (target: typeof merged) =>
      target
        .attr('class', (mark) => historyMarkClassName(mark.event.result))
        .attr('transform', (mark) => {
          const position = markPosition(mark, layout);
          return `translate(${position.x - historyGeometry.markWidth / 2} ${position.y}) rotate(${mark.rotation} ${historyGeometry.rotationCenterX} ${historyGeometry.rotationCenterY})`;
        });
    if (reduceMotion) applyMarks(merged);
    else
      merged
        .transition()
        .duration(160)
        .attr('class', (mark) => historyMarkClassName(mark.event.result))
        .attr('transform', (mark) => {
          const position = markPosition(mark, layout);
          return `translate(${position.x - historyGeometry.markWidth / 2} ${position.y}) rotate(${mark.rotation} ${historyGeometry.rotationCenterX} ${historyGeometry.rotationCenterY})`;
        });
    return () => {
      node.selectAll('*').interrupt();
    };
  }, [layout]);

  return (
    <div
      className="relative h-full min-h-44 w-max"
      style={{ height: layout.height, width: layout.width }}
    >
      <svg
        className="relative block h-full overflow-visible"
        ref={svgRef}
        height={layout.height}
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        width={layout.width}
      >
        <g className="history-d3-axis" />
        <g className="history-d3-marks" />
      </svg>
    </div>
  );
}
